import { useState } from "react";
import React from "react";
import "./CustomCalendar.css";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAYS = ["Mon","Tue","Wed","Thr","Fri","Sat","Sun"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

function isBefore(date, min) {
  if (!min) return false;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const m = new Date(min.getFullYear(), min.getMonth(), min.getDate());
  return d <= m;
}

function isAfter(date, max) {
  if (!max) return false;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const m = new Date(max.getFullYear(), max.getMonth(), max.getDate());
  return d > m;
}

function DayView({ year, month, selectedDate, onSelectDate, onChangeMonth, onClickYear,onClickMonth, minDate, maxDate }) {
  const today = new Date();
  const totalDays = getDaysInMonth(year, month);
  const firstDow = getFirstDayOfWeek(year, month);

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonthDays = getDaysInMonth(prevYear, prevMonth);
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;

  const cells = [];
  for (let i = firstDow - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, month: prevMonth, year: prevYear, other: true });
  }
  for (let d = 1; d <= totalDays; d++) {
    cells.push({ day: d, month, year, other: false });
  }
  let trailing = 1;
  while (cells.length < 42) {
    cells.push({ day: trailing++, month: nextMonth, year: nextYear, other: true });
  }

  const isToday = (c) => !c.other && c.day === today.getDate() && c.month === today.getMonth() && c.year === today.getFullYear();
  const isSelected = (c) => selectedDate && c.day === selectedDate.getDate() && c.month === selectedDate.getMonth() && c.year === selectedDate.getFullYear();
  const isWeekend = (idx) => idx % 7 === 5 || idx % 7 === 6;
  const isDisabled = (c) => {
    if (c.other) return true;
    const d = new Date(c.year, c.month, c.day);
    return isBefore(d, minDate) || isAfter(d, maxDate);
  };

  return (
    <div className="day-view">
      <div className="day-header-calendar">
        <button type="button" className="calendar-nav-btn" onClick={() => onChangeMonth(-1)}>&#8249;</button>
        <div className="day-header-center">
          <span className="month-label" onClick={onClickMonth} style={{ cursor: "pointer" }}>{MONTHS[month]}</span>
          <span className="year-label" onClick={onClickYear}>{year}</span>
        </div>
        <button type="button" className="calendar-nav-btn" onClick={() => onChangeMonth(1)}>&#8250;</button>
      </div>

      <div className="week-row">
        {DAYS.map((d, i) => (
          <span key={d} className={`week-day${i >= 5 ? " weekend" : ""}`}>{d}</span>
        ))}
      </div>

      <div className="days-grid">
        {cells.map((c, i) => {
          const tod = isToday(c);
          const sel = isSelected(c);
          const wk = isWeekend(i);
          const dis = isDisabled(c);

          const cellClass = ["day-cell-calendar", tod && !sel ? "today" : "", sel ? "selected" : "", dis ? "disabled" : ""].filter(Boolean).join(" ");
          const numClass = ["day-num", dis ? "disabled" : c.other ? "other" : wk ? "weekend" : "", tod && !sel && !dis ? "today" : "", sel ? "selected" : ""].filter(Boolean).join(" ");

          return (
            <div key={i} className={cellClass} onClick={() => !dis && onSelectDate(new Date(c.year, c.month, c.day))}>
              <span className={numClass}>{c.day}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ year, onSelectYear, minDate, maxDate }) {
  const [page, setPage] = useState(0);

  const base = Math.floor(year / 20) * 20;
  const start = base + 5 + (page * 20);
  const years = Array.from({ length: 20 }, (_, i) => start + i);

  const isYearDisabled = (y) => {
    if (minDate && y < minDate.getFullYear()) return true;
    if (maxDate && y > maxDate.getFullYear()) return true;
    return false;
  };

  return (
    <div className="year-view">
      <div className="range-header">
        <button type="button" className="calendar-nav-btn" onClick={() => setPage(p => p - 1)}>&#8249;</button>
        <span className="range-label">{years[0]} &nbsp;&#8211;&nbsp; {years[years.length - 1]}</span>
        <button type="button" className="calendar-nav-btn" onClick={() => setPage(p => p + 1)}>&#8250;</button>
      </div>

      <div className="year-grid">
        {years.map((y) => {
          const dis = isYearDisabled(y);
          return (
            <div key={y} className={`year-cell${y === year ? " selected" : ""}${dis ? " disabled" : ""}`} onClick={() => !dis && onSelectYear(y)}>
              <span className={`year-num${y === year ? " selected" : ""}${dis ? " disabled" : ""}`}>{y}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ year, month, onSelectMonth, minDate, maxDate }) {
  const isMonthDisabled = (i) => {
    if (minDate) {
      const lastDayOfMonth = new Date(year, i + 1, 0);
      if (lastDayOfMonth < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return true;
    }
    if (maxDate) {
      const firstDayOfMonth = new Date(year, i, 1);
      if (firstDayOfMonth > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) return true;
    }
    return false;
  };

  return (
    <div className="month-view">
      <div className="year-badge">{year}</div>
      <div className="month-grid">
        {MONTHS.map((m, i) => {
          const dis = isMonthDisabled(i);
          return (
            <div key={m} className={`month-cell${i === month ? " selected" : ""}${dis ? " disabled" : ""}`} onClick={() => !dis && onSelectMonth(i)}>
            <span className={`month-name${i === month ? " selected" : ""}${dis ? " disabled" : ""}`}>{m}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Calendar({
  value = null,
  onChange,
  defaultView = "day",
  showAllViews = false,
  minDate = null,
  maxDate = null,
}) {
  const init = value || new Date();
  const [view, setView] = useState(defaultView);
  const [month, setMonth] = useState(init.getMonth());
  const [year, setYear] = useState(init.getFullYear());

  const handleChangeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m);
    setYear(y);
  };

  const handleSelectDate = (date) => {
    onChange && onChange(date);
  };

  const handleSelectYear = (y) => {
    setYear(y);
    setView("month");
  };

  const handleSelectMonth = (m) => {
    setMonth(m);
    setView("day");
  };

  if (showAllViews) {
    return (
      <div className="calendar-all-views">
        <DayView year={year} month={month} selectedDate={value} onSelectDate={handleSelectDate} onChangeMonth={handleChangeMonth} minDate={minDate} maxDate={maxDate} />
        <div className="calendar-divider" />
        <YearView year={year} onSelectYear={handleSelectYear} minDate={minDate} maxDate={maxDate} />
        <div className="calendar-divider" />
        <MonthView year={year} month={month} onSelectMonth={handleSelectMonth} minDate={minDate} maxDate={maxDate} />
      </div>
    );
  }

  return (
    <div className="calendar-wrapper">
      {view === "day" && <DayView year={year} month={month} selectedDate={value} onSelectDate={handleSelectDate} onChangeMonth={handleChangeMonth} onClickYear={() => setView("year")} onClickMonth={() => setView("month")} minDate={minDate} maxDate={maxDate} />}
      {view === "year" && <YearView year={year} onSelectYear={handleSelectYear} minDate={minDate} maxDate={maxDate} />}
      {view === "month" && <MonthView year={year} month={month} onSelectMonth={handleSelectMonth} minDate={minDate} maxDate={maxDate} />}
    </div>
  );
}